import { Component, Fragment } from 'react';
import {
  makeSelectUploadDialogueError,
  makeSelectedSnapshot,
} from './selectors';

import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { isNonNullable } from '@ipfabric/utils';
import { JOB_ACTIONS } from 'containers/App/Header/JobManagement/constants';
import { makeSelectJobs } from 'containers/App/Header/JobManagement/selectors';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withAppProps } from 'utils/react/withAppProps';
import BandwidthChart from './BandwidthChart';
import Buttons from './Buttons';
import ContentDetail from './ContentDetail';
import InfoBoxes from './InfoBoxes';
import SnapshotLoadingBar from './SnapshotLoadingBar';
import Tabs from './Tabs';
import { LOADED_STATES } from './constants';

const spinnerIcon = 'fa-regular fa-spinner fa-spin fa-fw';

@withAppProps
class Content extends Component {
  static propTypes = {
    allMsg: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired,
    currentSnapshotId: ImmutablePropTypes.list.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    exceptionsMsg: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    hostnameTableDetailMsg: PropTypes.object.isRequired,
    jobs: ImmutablePropTypes.list,
    loadingJob: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    msg: PropTypes.object.isRequired,
    newSnapshot: PropTypes.bool.isRequired,
    onAddLabel: PropTypes.func.isRequired,
    onClearSitesFilter: PropTypes.func.isRequired,
    onCloseSettings: PropTypes.func,
    recalculating: PropTypes.bool,
    scheduledJob: PropTypes.bool,
    selectedSite: ImmutablePropTypes.list.isRequired,
    selectedSnapshot: PropTypes.string,
    showSettings: PropTypes.bool.isRequired,
    snapshotFinishing: PropTypes.bool.isRequired,
    snapshotFromParamsIsInvalid: PropTypes.bool,
    snapshotId: PropTypes.string,
    snapshotLoading: PropTypes.bool,
    snapshotRunning: PropTypes.bool.isRequired,
    snapshotState: PropTypes.string,
    snapshotStatus: PropTypes.string,
  };

  static defaultProps = {
    scheduledJob: false,
  };

  shouldComponentUpdate(nextProps) {
    const {
      currentSnapshotId,
      jobs,
      loadingJob,
      location,
      selectedSite,
      showSettings,
      snapshotId,
      snapshotRunning,
      snapshotState,
      snapshotStatus,
    } = this.props;
    return (
      currentSnapshotId !== nextProps.currentSnapshotId ||
      loadingJob !== nextProps.loadingJob ||
      selectedSite !== nextProps.selectedSite ||
      snapshotId !== nextProps.snapshotId ||
      snapshotRunning !== nextProps.snapshotRunning ||
      snapshotState !== nextProps.snapshotState ||
      snapshotStatus !== nextProps.snapshotStatus ||
      jobs !== nextProps.jobs ||
      showSettings !== nextProps.showSettings ||
      location !== nextProps.location
    );
  }

  getSnapshotDetail = (canEdit) => {
    const {
      onAddLabel,
      msg,
      dateTimeFormat,
      snapshotStatus,
      snapshotId,
      selectedSite,
    } = this.props;
    return (
      <ContentDetail
        onAddLabel={onAddLabel}
        canEdit={canEdit}
        snapshotId={snapshotId}
        msg={msg}
        dateTimeFormat={dateTimeFormat}
        snapshotStatus={snapshotStatus}
        selectedSite={selectedSite}
      />
    );
  };

  getActions = (isRunning, loadingJob) => {
    const {
      auth,
      currentSnapshotId,
      dateTimeFormat,
      jobs,
      msg,
      selectedSnapshot,
      snapshotId,
      snapshotRunning,
      onClearSitesFilter,
      showSettings,
    } = this.props;
    const hasWritePermission = auth.scope.includes('write');
    return (
      <Buttons
        currentSnapshotId={currentSnapshotId}
        dateTimeFormat={dateTimeFormat}
        hasWritePermission={hasWritePermission}
        isLoading={loadingJob}
        isRunning={isRunning}
        jobs={jobs}
        msg={msg}
        onClearSitesFilter={onClearSitesFilter}
        selectedSnapshot={selectedSnapshot}
        showSettings={showSettings}
        snapshotId={snapshotId}
        snapshotRunning={snapshotRunning}
        spinnerIcon={spinnerIcon}
      />
    );
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
  getSnapshotContent = () => {
    const {
      auth,
      currentSnapshotId,
      exceptionsMsg,
      hostnameTableDetailMsg,
      jobs,
      loadingJob,
      msg,
      newSnapshot,
      scheduledJob,
      selectedSnapshot,
      snapshotFinishing,
      snapshotFromParamsIsInvalid,
      snapshotRunning,
      snapshotId,
      snapshotLoading,
      snapshotStatus,
      snapshotState,
      recalculating,
    } = this.props;
    const hasWritePermission = auth.scope.includes('write');
    if (currentSnapshotId.size > 1) {
      return (
        <div className='p-lgu-6'>
          {this.getActions(snapshotRunning, loadingJob)}
          <div>
            <h4 className='mt-lgu-3'>
              {get(msg, 'detail.selectedSnapshots', '').replace(
                '{totalSnapshots}',
                currentSnapshotId.size,
              )}
            </h4>
          </div>
        </div>
      );
    }
    const canEdit = hasWritePermission && !snapshotRunning;
    const foundJob =
      isNonNullable(snapshotId) &&
      jobs.find((job) => job.get('snapshot') === snapshotId);
    if (newSnapshot || !currentSnapshotId.size) {
      return (
        <Fragment>
          <div className='snapshots-content__info'>
            <div className='mb-5'>
              {this.getActions(snapshotRunning, loadingJob)}
            </div>
            <InfoBoxes
              msg={msg}
              isRunning={snapshotRunning}
              selectedSnapshot={selectedSnapshot}
              snapshotId={snapshotId}
            />
            <BandwidthChart
              msg={get(msg, 'discovery.bandwidth')}
              newSnapshot={newSnapshot}
            />
          </div>
          <Tabs
            msg={msg}
            snapshotId={snapshotId}
            isRunning={snapshotRunning}
            isLoading={loadingJob}
            newSnapshot={newSnapshot}
            scheduledJob={scheduledJob}
            snapshotDone
            selectedSnapshot={selectedSnapshot}
            exceptionsMsg={exceptionsMsg}
            hostnameTableDetailMsg={hostnameTableDetailMsg}
          />
        </Fragment>
      );
    }
    if (foundJob) {
      const snapshotDone =
        isNonNullable(snapshotId) && snapshotStatus === 'done';
      if (foundJob.get('action') === JOB_ACTIONS.snapshotDelete) {
        return (
          <Fragment>
            <div className='snapshots-content__info'>
              <div className='mb-5'>
                {this.getActions(snapshotRunning, loadingJob)}
              </div>
              {this.getSnapshotDetail(canEdit)}
            </div>
            {selectedSnapshot && (
              <Tabs
                msg={msg}
                snapshotId={snapshotId}
                isRunning={snapshotRunning}
                isLoading={loadingJob}
                newSnapshot={newSnapshot}
                scheduledJob={scheduledJob}
                snapshotDone={snapshotDone}
                selectedSnapshot={selectedSnapshot}
                exceptionsMsg={exceptionsMsg}
                hostnameTableDetailMsg={hostnameTableDetailMsg}
              />
            )}
          </Fragment>
        );
      }
    }
    if (isNonNullable(snapshotId)) {
      if (snapshotState === 'unloading' || snapshotState === 'deleting') {
        return (
          <div className='snapshots-content__info'>
            <div className='mb-5'>
              {this.getActions(snapshotRunning, loadingJob)}
            </div>
            {this.getSnapshotDetail(canEdit)}
          </div>
        );
      }
      const snapshotDone = snapshotStatus === 'done';
      if (
        (snapshotState === 'discovering' &&
          (snapshotStatus === 'finishing' || snapshotDone)) ||
        (snapshotState === 'loading' &&
          (snapshotStatus === 'finishing' || snapshotDone)) ||
        recalculating ||
        snapshotFinishing
      ) {
        return (
          <Fragment>
            <div className='snapshots-content__info'>
              <div className='mb-5'>
                {this.getActions(snapshotRunning, loadingJob)}
              </div>
              <LoaderAndText
                text={<span>{get(msg, 'discovery.finish.mainMsg', '')}</span>}
                bottomText={get(msg, 'discovery.finish.bottomText', '')}
                delay={20000}
              />
            </div>
            {selectedSnapshot && (
              <Tabs
                msg={msg}
                snapshotId={snapshotId}
                isRunning={snapshotRunning}
                isLoading={loadingJob}
                newSnapshot={newSnapshot}
                scheduledJob={scheduledJob}
                snapshotDone={snapshotDone}
                selectedSnapshot={selectedSnapshot}
                exceptionsMsg={exceptionsMsg}
                hostnameTableDetailMsg={hostnameTableDetailMsg}
                recalculating={recalculating}
              />
            )}
          </Fragment>
        );
      }
      if (snapshotLoading) {
        return (
          <Fragment>
            <div className='snapshots-content__info'>
              <div className='mb-5'>
                {this.getActions(snapshotRunning, loadingJob)}
              </div>
              <SnapshotLoadingBar msg={msg} snapshotId={snapshotId} />
            </div>
            {selectedSnapshot && (
              <Tabs
                msg={msg}
                snapshotId={snapshotId}
                isRunning={snapshotRunning}
                isLoading={loadingJob}
                newSnapshot={newSnapshot}
                scheduledJob={scheduledJob}
                snapshotDone={snapshotDone}
                selectedSnapshot={selectedSnapshot}
                exceptionsMsg={exceptionsMsg}
                hostnameTableDetailMsg={hostnameTableDetailMsg}
              />
            )}
          </Fragment>
        );
      }
      if (snapshotRunning && !snapshotDone) {
        return (
          <Fragment>
            <div className='snapshots-content__info'>
              <div className='mb-5'>
                {this.getActions(snapshotRunning, loadingJob)}
              </div>
              <InfoBoxes
                msg={msg}
                isRunning={snapshotRunning}
                selectedSnapshot={selectedSnapshot}
                snapshotId={snapshotId}
              />
              <BandwidthChart msg={get(msg, 'discovery.bandwidth')} />
            </div>
            {selectedSnapshot && (
              <Tabs
                msg={msg}
                snapshotId={snapshotId}
                isRunning={snapshotRunning}
                isLoading={loadingJob}
                newSnapshot={newSnapshot}
                scheduledJob={scheduledJob}
                snapshotDone={snapshotDone}
                selectedSnapshot={selectedSnapshot}
                exceptionsMsg={exceptionsMsg}
                hostnameTableDetailMsg={hostnameTableDetailMsg}
              />
            )}
          </Fragment>
        );
      }
      if (LOADED_STATES.indexOf(snapshotState) > -1) {
        return (
          <Fragment>
            <div className='snapshots-content__info'>
              <div className='mb-5'>
                {this.getActions(snapshotRunning, loadingJob)}
              </div>
              {this.getSnapshotDetail(canEdit)}
            </div>
            {selectedSnapshot && (
              <Tabs
                msg={msg}
                snapshotId={snapshotId}
                isRunning={snapshotRunning}
                isLoading={loadingJob}
                newSnapshot={newSnapshot}
                scheduledJob={scheduledJob}
                snapshotDone={snapshotDone}
                selectedSnapshot={selectedSnapshot}
                exceptionsMsg={exceptionsMsg}
                hostnameTableDetailMsg={hostnameTableDetailMsg}
              />
            )}
          </Fragment>
        );
      }
      return (
        <div className='snapshots-content__info'>
          <div className='mb-5'>
            {this.getActions(snapshotRunning, loadingJob)}
          </div>
          {this.getSnapshotDetail(canEdit)}
        </div>
      );
    }

    return (
      <div className='flex h-full items-center justify-center'>
        <h3 className='m-0'>
          {snapshotFromParamsIsInvalid
            ? msg.snapshotNotFound
            : msg.unknownSnapshotError}
        </h3>
      </div>
    );
  };

  render() {
    return (
      <div
        id='snapshot-management-content'
        className='snapshots-right-wrapper relative overflow-x-hidden'
      >
        <ScrollView
          className='snapshots-content__wrapper-scroll'
          contentClassName='flex flex-col'
        >
          {this.getSnapshotContent()}
        </ScrollView>
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  selectedSnapshot: makeSelectedSnapshot(),
  uploadDialogueError: makeSelectUploadDialogueError(),
  jobs: makeSelectJobs(),
});

export default connect(mapStateToProps)(Content);
